import { useCallback, useMemo, useState } from "react"
import { UseSearchSortProps, useSearchSort } from "shared-ts/hooks/useSort"
import { SortOrder, SortT } from "shared-ts/hooks/useSort/helpers"
import {
  UseSearchSortRes,
  UseSortProps,
  UseSortRes,
  UseWebSortProps,
} from "shared-ts/hooks/useSort/types"
import { useBaseSort } from "shared-ts/hooks/useSort/useSort"

import { useUrlQuery } from "./useUrlQuery"

const SORT_PARAM = "sort"
const ORDER_PARAM = "order"

export type UseWebSearchSortProps<ItemT> = Omit<
  UseSearchSortProps<ItemT>,
  "useSort"
> & {
  persistInUrl?: boolean
}

export function useWebSort<ItemT, OrderT>(
  props: UseWebSortProps<ItemT, OrderT>
): UseSortRes<ItemT, OrderT> {
  const { persistInUrl = true, ...restProps } = props

  const localRes = useLocalSort(restProps)
  const urlRes = useUrlSort(restProps)

  return useMemo(() => {
    return persistInUrl === false ? localRes : urlRes
  }, [persistInUrl, localRes, urlRes])
}

export function useWebSearchSort<ItemT>(
  props: UseWebSearchSortProps<ItemT>
): UseSearchSortRes<ItemT> {
  const { persistInUrl, ...rest } = props

  return useSearchSort<ItemT>({
    ...rest,
    useSort: (props) => useWebSort({ ...props, persistInUrl }),
  })
}

function useUrlSort<ItemT, OrderT>(
  props: UseSortProps<ItemT, OrderT>
): UseSortRes<ItemT, OrderT> {
  const {
    items,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    orderExtractor,
    defaultOrder = SortOrder.Ascending,
  } = props

  const { query, pushQuery } = useUrlQuery()
  const sortQueryParam = query[SORT_PARAM]
  const orderQueryParam = query[ORDER_PARAM]

  const normalizedSortQueryParam = useMemo<string | null>(() => {
    if (sortQueryParam === undefined || Array.isArray(sortQueryParam)) {
      return null
    }
    return sortQueryParam
  }, [sortQueryParam])

  const urlOrder = useMemo<SortOrder | null>(() => {
    if (
      orderQueryParam === undefined ||
      Array.isArray(orderQueryParam) ||
      !Object.values(SortOrder).includes(orderQueryParam as SortOrder)
    ) {
      return null
    }
    return orderQueryParam as SortOrder
  }, [orderQueryParam])

  const onSelectSortBy = useCallback(
    (sort: ItemT | null) => {
      const newQuery = { ...query }
      if (sort) {
        newQuery[SORT_PARAM] = keyExtractor(sort)
        if (!newQuery[ORDER_PARAM]) {
          newQuery[ORDER_PARAM] = defaultOrder
        }
      } else {
        delete newQuery[SORT_PARAM]
        delete newQuery[ORDER_PARAM]
      }
      pushQuery(newQuery)
    },
    [query, pushQuery, keyExtractor, defaultOrder]
  )

  const onSelectDirection = useCallback(
    (direction: SortOrder) => {
      pushQuery({ ...query, [ORDER_PARAM]: direction })
    },
    [query, pushQuery]
  )

  const sort = useMemo<SortT<ItemT> | null>(() => {
    if (!normalizedSortQueryParam) {
      return null
    }
    const value = items.find(
      (item) => keyExtractor(item) === normalizedSortQueryParam
    )
    if (!value) {
      return null
    }
    return {
      value,
      order: urlOrder ?? defaultOrder,
    }
  }, [items, normalizedSortQueryParam, urlOrder, keyExtractor, defaultOrder])

  const order = useMemo<OrderT>(
    () => orderExtractor(urlOrder),
    [urlOrder, orderExtractor]
  )

  return {
    sortProps: {
      sort,
      items,
      onSelectSortBy,
      keyExtractor,
      valueExtractor: labelExtractor,
      accessoryExtractor,
      onSelectDirection,
    },
    order,
  }
}

function useLocalSort<ItemT, OrderT>(
  props: UseSortProps<ItemT, OrderT>
): UseSortRes<ItemT, OrderT> {
  const [localSort, setLocalSort] = useState<string | null>(null)
  const [localOrder, setLocalOrder] = useState<SortOrder | null>(null)

  return useBaseSort({
    ...props,
    sortValue: localSort,
    orderValue: localOrder,
    setSortValue: setLocalSort,
    setOrderValue: setLocalOrder,
  })
}
